import React, { useState, useEffect } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import './css_pages/ProductTypes.css';
import { getAllProductTypes, createProductType, updateProductType, deleteProductType } from '../services/productTypesService';

function ProductTypes() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newProductType, setNewProductType] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar tipos de productos al montar el componente
  useEffect(() => {
    fetchProductTypes();
  }, []);

  // Obtener todos los tipos de productos desde la API
  const fetchProductTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllProductTypes();
      if (response.success) {
        setProductTypes(response.data);
      }
    } catch (error) {
      setError('Error loading product types');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar tipos de productos según búsqueda
  const filteredTypes = productTypes.filter(type =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Abrir modal para crear
  const handleOpenModal = () => {
    setShowModal(true);
    setNewProductType('');
    setEditingId(null);
  };

  // Abrir modal para editar
  const handleEditModal = (type) => {
    setShowModal(true);
    setNewProductType(type.name);
    setEditingId(type.id);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setNewProductType('');
    setEditingId(null);
  };

  // Guardar o actualizar tipo de producto
  const handleSaveProductType = async () => {
    const normalizedName = newProductType.trim();
    if (normalizedName === '') {
      alert('Please enter a name');
      return;
    }
    try {
      setLoading(true);
      if (editingId) {
        const response = await updateProductType(editingId, {name: normalizedName});
        if (response.success) {
          alert('Product type successfully updated');
          await fetchProductTypes();
          handleCloseModal();
        }
      }else{
        const response = await createProductType({ name: normalizedName});
        if (response.success) {
          alert('Type of product successfully created');
          await fetchProductTypes();
          handleCloseModal();
      }
    }
  } catch (error) {
    if (error.response && error.response.status === 409) {
      alert('There is already a product type with that name');
      } else {
        alert('Error saving product type');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal de confirmación de eliminación
  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Cerrar modal de eliminación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteProductType(deleteId);
      if (response.success) {
        alert('Product type successfully removed');
        await fetchProductTypes();
      }
      handleCloseDeleteModal();
    } catch (error) {
      alert('Error deleting product type');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="product-types-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>Product types</h1>
        </div>
        <button className="btn-primary" onClick={handleOpenModal} disabled={loading}>
          <FaPlus /> Create new product type
        </button>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchProductTypes}>Retry</button>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table - Desktop */}
      <div className="table-container desktop-only">
        {loading && <div className="loading">Loading...</div>}
        
        <table className="data-table">
          <thead>
            <tr>
              <th>Name ▼</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTypes.length === 0 ? (
              <tr>
                <td colSpan="2" style={{ textAlign: 'center', padding: '40px' }}>
                  {loading ? 'Loading...' : 'No results found'}
                </td>
              </tr>
            ) : (
              filteredTypes.map((type) => (
                <tr key={type.id}>
                  <td>{type.name}</td>
                  <td>
                    <button 
                      className="btn-action" 
                      onClick={() => handleEditModal(type)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-action btn-delete" 
                      onClick={() => handleOpenDeleteModal(type.id)}
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cards - Mobile */}
      <div className="product-types-cards-container mobile-only">
        {loading && <div className="loading">Loading...</div>}
        
        {filteredTypes.length === 0 ? (
          <div className="no-results">
            {loading ? 'Loading...' : 'No results found'}
          </div>
        ) : (
          filteredTypes.map((type) => (
            <div key={type.id} className="product-type-card">
              <div className="product-type-card-header">
                <h3 className="product-type-card-name">{type.name}</h3>
              </div>

              <div className="product-type-card-footer">
                <button 
                  className="btn-card-action btn-edit" 
                  onClick={() => handleEditModal(type)}
                  disabled={loading}
                >
                  Edit
                </button>
                <button 
                  className="btn-card-action btn-delete-card" 
                  onClick={() => handleOpenDeleteModal(type.id)}
                  disabled={loading}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit product type' : 'Create new product type'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newProductType}
                  onChange={(e) => setNewProductType(e.target.value)}
                  placeholder="Enter the product type name"
                  autoFocus
                  disabled={loading}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal} disabled={loading}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveProductType} disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminación */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm deletion</h2>
              <button className="btn-close" onClick={handleCloseDeleteModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to remove this type of product?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleConfirmDelete} disabled={loading}>
                {loading ? 'Removing...' : 'Eliminate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductTypes;